import { groupSlotsByDateInTz } from "./state.js";
import { displayTimezone, listTimeZones, zoneCity } from "./timezone.js";
import { slotAvailabilityNote, slotTime, slotTimeWithZones } from "./viewerTime.js";

// Mise en page adaptative (modèle HubSpot Meetings) :
// - mode "wide" (conteneur ≥ 600 px) : calendrier | créneaux côte à côte, puis
//   formulaire plein cadre ;
// - mode "narrow" : un panneau par étape (jour → créneau → coordonnées).
// Le mode est mesuré sur le conteneur (index.js) et porté par state.mode.

export function render(container, state, messages, config, handlers) {
  const groups = groupSlotsByDateInTz(state.slots, displayTimezone(state, config));
  const currentSlots = state.selectedDate ? groups[state.selectedDate] || [] : [];

  container.innerHTML = renderToHtml(state, messages, config, groups);

  container.querySelector?.("[data-rdv-timezone]")?.addEventListener("change", (event) => {
    handlers.onSelectTimezone(event.target.value);
  });

  container.querySelectorAll("[data-rdv-slot]").forEach((button) => {
    const index = Number(button.getAttribute("data-rdv-slot"));
    button.addEventListener("click", () => handlers.onSelectSlot(currentSlots[index]));
  });

  container.querySelectorAll("[data-rdv-back]").forEach((button) => {
    button.addEventListener("click", () => {
      const form = container.querySelector("[data-rdv-form]");
      handlers.onBackToSlots(form ? readForm(form) : state.form);
    });
  });

  container.querySelectorAll("[data-rdv-back-date]").forEach((button) => {
    button.addEventListener("click", () => {
      const form = container.querySelector("[data-rdv-form]");
      handlers.onBackToDate(form ? readForm(form) : state.form);
    });
  });

  const form = container.querySelector("[data-rdv-form]");
  if (form) {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      handlers.onSubmit(readForm(form));
    });
  }
}

export function renderToHtml(state, messages, config, groups = groupSlotsByDateInTz(state.slots, displayTimezone(state, config))) {
  const mode = state.mode === "wide" ? "wide" : "narrow";
  const screen = currentScreen(state);
  const currentSlots = state.selectedDate ? groups[state.selectedDate] || [] : [];
  const hasDates = Object.keys(groups).length > 0;

  let body;
  if (screen === "success") {
    body = renderConfirmation(state, messages, config);
  } else if (screen === "form") {
    body = renderDetails(state, messages, config);
  } else if (mode === "wide") {
    body = `
      <div class="rdv-pick">
        ${renderCalendarColumn(state, messages, hasDates)}
        ${renderSlotsColumn(currentSlots, state, messages, config, { mode })}
      </div>
    `;
  } else if (screen === "slot" && state.selectedDate) {
    body = `<div class="rdv-pick">${renderSlotsColumn(currentSlots, state, messages, config, { mode })}</div>`;
  } else {
    body = `<div class="rdv-pick">${renderCalendarColumn(state, messages, hasDates)}</div>`;
  }

  const showHeader = screen !== "form" && screen !== "success";

  return `
    <div class="rdv-root" lang="${escapeHtml(messages.lang || "fr")}" data-rdv-theme="${escapeHtml(config.theme || "light")}" data-rdv-mode="${mode}" data-rdv-screen="${screen}">
      <div class="rdv-card">
        ${showHeader ? renderHeader(config, messages) : ""}
        ${renderStatus(state, messages, screen)}
        ${body}
      </div>
    </div>
  `;
}

function currentScreen(state) {
  if (state.status === "success" && state.confirmation) {
    return "success";
  }
  if (state.selectedSlot && state.screen === "form") {
    return "form";
  }
  return state.screen === "slot" ? "slot" : "date";
}

// En-tête léger : logo/photo et nom facultatifs (marque blanche : rien par
// défaut), titre du rendez-vous, durée.
function renderHeader(config, messages) {
  const brandName = config.brandName || "";
  const logo = config.brandLogo
    ? `<span class="rdv-head-logo"><img src="${escapeHtml(config.brandLogo)}" alt="${escapeHtml(brandName)}"></span>`
    : "";
  return `
    <header class="rdv-head">
      ${logo}
      <div class="rdv-head-text">
        ${brandName ? `<p class="rdv-head-brand">${escapeHtml(brandName)}</p>` : ""}
        <h2 class="rdv-head-title">${escapeHtml(config.appointmentTitle || messages.appointmentTitle)}</h2>
        ${config.appointmentDuration ? `<p class="rdv-head-meta">${escapeHtml(config.appointmentDuration)}</p>` : ""}
      </div>
    </header>
  `;
}

// Message d'état (chargement, envoi, erreur, aucun créneau) : affiché seulement
// quand il a quelque chose à dire. Cible de revealFeedback en cas d'erreur.
function renderStatus(state, messages, screen) {
  if (screen === "success") {
    return "";
  }
  const text = statusMessage(state, messages);
  if (!text) {
    return "";
  }
  const isError = state.status === "error" || Boolean(state.error);
  return `
    <div class="rdv-status-wrap">
      <div class="rdv-status" aria-live="polite" ${isError ? 'role="alert"' : ""}>
        <span class="rdv-status-dot" aria-hidden="true"></span>
        <span>${escapeHtml(text)}</span>
      </div>
    </div>
  `;
}

function statusMessage(state, messages) {
  if (state.status === "loading") {
    return messages.loading;
  }
  if (state.status === "submitting") {
    return messages.submitting;
  }
  if (state.error || state.status === "error") {
    return errorMessage(state.error, messages);
  }
  if (state.status === "empty") {
    return messages.empty;
  }
  return "";
}

function renderCalendarColumn(state, messages, hasDates) {
  let content;
  if (state.status === "loading") {
    content = `
      <div class="rdv-calendar-shell rdv-calendar-shell-loading" aria-hidden="true">
        <div class="rdv-calendar-skeleton rdv-calendar-skeleton-head"></div>
        <div class="rdv-calendar-skeleton-grid">
          ${Array.from({ length: 35 }, () => '<span class="rdv-calendar-skeleton rdv-calendar-skeleton-day"></span>').join("")}
        </div>
      </div>
    `;
  } else if (!hasDates) {
    content = `<p class="rdv-muted rdv-empty-card">${escapeHtml(messages.empty)}</p>`;
  } else {
    content = `
      <div class="rdv-calendar-shell">
        <input class="rdv-calendar-input" type="text" data-rdv-calendar-input tabindex="-1" aria-hidden="true">
      </div>
    `;
  }

  return `
    <section class="rdv-col rdv-col-calendar" aria-labelledby="rdv-date-title">
      <h3 class="rdv-col-title" id="rdv-date-title">${escapeHtml(messages.chooseDate)}</h3>
      ${content}
    </section>
  `;
}

function renderSlotsColumn(slots, state, messages, config, { mode }) {
  const displayTz = displayTimezone(state, config);
  const back = mode === "narrow"
    ? `<button class="rdv-back" type="button" data-rdv-back-date>${escapeHtml(messages.back)}</button>`
    : "";

  let list;
  if (!state.selectedDate) {
    list = `<p class="rdv-muted rdv-slots-placeholder">${escapeHtml(messages.pickDayFirst)}</p>`;
  } else if (slots.length === 0) {
    list = `<p class="rdv-muted rdv-empty-card">${escapeHtml(messages.emptyDate)}</p>`;
  } else {
    list = `
      <div class="rdv-slot-list">
        ${slots
          .map((slot, index) => {
            // Seule information utile sous l'heure : l'heure du commerçant quand
            // elle diffère (« 09:00 à Guadeloupe ») ; jamais un « Disponible » seul.
            const full = slotAvailabilityNote(slot, displayTz, config.timezone, messages);
            const note = full === messages.slotAvailable ? "" : full.replace(`${messages.slotAvailable} · `, "");
            return `
              <button class="rdv-button rdv-slot-button" type="button" data-rdv-slot="${index}" aria-pressed="${state.selectedSlot?.start === slot.start}">
                <span class="rdv-slot-time">${escapeHtml(slotTime(slot, displayTz))}</span>
                ${note ? `<span class="rdv-slot-note">${escapeHtml(note)}</span>` : ""}
              </button>
            `;
          })
          .join("")}
      </div>
    `;
  }

  return `
    <section class="rdv-col rdv-col-slots" aria-labelledby="rdv-slot-title">
      ${back}
      <h3 class="rdv-col-title" id="rdv-slot-title">${escapeHtml(messages.chooseSlot)}</h3>
      ${state.selectedDate ? `<p class="rdv-col-sub">${escapeHtml(messages.slotsFor)} <strong>${escapeHtml(formatLongDate(state.selectedDate, messages))}</strong></p>` : ""}
      ${renderTimezoneSelector(state, config, messages)}
      ${list}
    </section>
  `;
}

// Sélecteur de fuseau (à la Calendly) : par défaut le fuseau du visiteur,
// modifiable ; tous les créneaux se recalculent dans le fuseau choisi.
function renderTimezoneSelector(state, config, messages) {
  const current = displayTimezone(state, config);
  if (!current) {
    return "";
  }
  const zones = listTimeZones();
  const options = (zones.includes(current) ? zones : [current, ...zones])
    .map((tz) => `<option value="${escapeHtml(tz)}"${tz === current ? " selected" : ""}>${escapeHtml(tzOptionLabel(tz))}</option>`)
    .join("");
  return `
    <label class="rdv-tz">
      <span class="rdv-tz-label">${escapeHtml(messages.timezone)}</span>
      <select class="rdv-tz-select" data-rdv-timezone aria-label="${escapeHtml(messages.timezone)}">${options}</select>
    </label>
  `;
}

function tzOptionLabel(tz) {
  return tz === "UTC" ? "UTC" : `${zoneCity(tz)} (${tz})`;
}

// Formulaire plein cadre : rappel du créneau + « Modifier », champs, mention
// RGPD, barre d'actions Retour / Confirmer.
function renderDetails(state, messages, config) {
  const showPhone = config.collectPhone !== false;
  const showMessage = config.collectMessage !== false;
  const submitting = state.status === "submitting";
  const when = `${formatLongDate(state.selectedSlot.date, messages)}, ${slotTimeWithZones(state.selectedSlot, displayTimezone(state, config), config.timezone, messages)}`;

  return `
    <section class="rdv-details" aria-labelledby="rdv-form-title">
      <h2 class="rdv-details-title" id="rdv-form-title">${escapeHtml(messages.formTitle)}</h2>
      <p class="rdv-details-recap">
        <span>${escapeHtml(when)}</span>
        <button class="rdv-link" type="button" data-rdv-back>${escapeHtml(messages.changeSlot)}</button>
      </p>
      <form class="rdv-form" data-rdv-form>
        ${field("name", messages.name, "text", state)}
        ${field("email", messages.email, "email", state)}
        ${showPhone ? field("phone", fieldLabel(messages.phone, config.phoneRequired, messages), "tel", state, { required: config.phoneRequired, help: config.phoneHelpText }) : ""}
        ${showMessage ? messageField(state, messages, config) : ""}
        ${renderPrivacyNotice(config, messages)}
        <div class="rdv-footer-bar">
          <button class="rdv-button rdv-secondary" type="button" data-rdv-back>${escapeHtml(messages.back)}</button>
          <button class="rdv-button rdv-submit" type="submit" ${submitting ? "disabled" : ""}>${escapeHtml(submitting ? messages.submitting : messages.submit)}</button>
        </div>
      </form>
    </section>
  `;
}

function field(name, label, type, state, { required = true, help = "" } = {}) {
  const error = state.fields?.[name] || "";
  return `
    <label class="rdv-field">
      <span class="rdv-label">${escapeHtml(label)}</span>
      <input class="rdv-input" name="${name}" type="${type}" value="${escapeHtml(state.form[name] || "")}" aria-describedby="rdv-${name}-error"${required ? " required" : ""}>
      ${help ? `<span class="rdv-hint">${escapeHtml(help)}</span>` : ""}
      <span class="rdv-field-error" id="rdv-${name}-error">${escapeHtml(error)}</span>
    </label>
  `;
}

// Libellé avec « (facultatif) » quand le champ n'est pas obligatoire.
function fieldLabel(label, required, messages) {
  return required ? label : `${label} (${messages.optional})`;
}

// Champ message : textarea avec aide, pré-remplissage (state.form.message) et
// obligation pilotée par l'API (config.messageRequired).
function messageField(state, messages, config) {
  const error = state.fields?.message || "";
  return `
    <label class="rdv-field">
      <span class="rdv-label">${escapeHtml(fieldLabel(messages.message, config.messageRequired, messages))}</span>
      <textarea class="rdv-textarea" name="message" aria-describedby="rdv-message-error"${config.messageRequired ? " required" : ""}>${escapeHtml(state.form.message)}</textarea>
      ${config.messageHelpText ? `<span class="rdv-hint">${escapeHtml(config.messageHelpText)}</span>` : ""}
      <span class="rdv-field-error" id="rdv-message-error">${escapeHtml(error)}</span>
    </label>
  `;
}

function renderConfirmation(state, messages, config) {
  // L'API renvoie « pending_confirmation » : le visiteur doit encore cliquer sur
  // le lien reçu par e-mail. Ne jamais annoncer « confirmé » dans ce cas.
  const pending = state.confirmation.status === "pending_confirmation";
  const title = pending ? messages.pendingTitle : messages.confirmationTitle;
  const intro = pending ? messages.pendingIntro : messages.confirmationIntro;
  const next = pending ? messages.pendingNext : messages.confirmationNext;
  return `
    <section class="rdv-confirmation" aria-labelledby="rdv-confirmation-title">
      <div class="rdv-success-mark" aria-hidden="true">${pending ? "✉" : "✓"}</div>
      <h2 class="rdv-title" id="rdv-confirmation-title">${escapeHtml(title)}</h2>
      <p>${escapeHtml(intro)}</p>
      <dl class="rdv-confirmation-list">
        <div><dt>${escapeHtml(messages.dateLabel)}</dt><dd>${escapeHtml(formatLongDate(state.confirmation.date, messages))}, ${escapeHtml(slotTimeWithZones(state.confirmation, displayTimezone(state, config), config.timezone, messages))}</dd></div>
        <div><dt>${escapeHtml(messages.name)}</dt><dd>${escapeHtml(state.confirmation.name)}</dd></div>
        <div><dt>${escapeHtml(messages.email)}</dt><dd>${escapeHtml(state.confirmation.email)}</dd></div>
      </dl>
      <p class="rdv-confirmation-note">${escapeHtml(next)}</p>
    </section>
  `;
}

function errorMessage(error, messages) {
  if (!error) {
    return messages.network;
  }

  // Le message de l'API est rédigé en français : il n'est repris tel quel que
  // pour un widget en français, sinon on affiche la traduction du code.
  const apiMessage = (messages.lang || "fr") === "fr" ? error.message : null;

  if (error.code && messages[error.code]) {
    return apiMessage || messages[error.code];
  }

  return apiMessage || messages.network;
}

// « lundi 12 octobre 2026 », « Monday, 12 October 2026 », « lunes, 12 de octubre de 2026 »
function formatLongDate(date, messages) {
  try {
    return new Intl.DateTimeFormat(messages.dateLocale || "fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(new Date(`${date}T12:00:00`));
  } catch (_error) {
    return date;
  }
}

function readForm(form) {
  const formData = new FormData(form);
  return {
    name: String(formData.get("name") || ""),
    email: String(formData.get("email") || ""),
    phone: String(formData.get("phone") || ""),
    message: String(formData.get("message") || "")
  };
}

function renderPrivacyNotice(config, messages) {
  if (!config.privacyNotice && !config.privacyPolicyUrl) {
    return "";
  }

  const notice = config.privacyNotice || messages.privacyDefaultNotice;
  const link = config.privacyPolicyUrl
    ? `<a class="rdv-privacy-link" href="${escapeHtml(config.privacyPolicyUrl)}" target="_blank" rel="noreferrer noopener">${escapeHtml(messages.privacyPolicyLabel)}</a>`
    : "";

  return `
    <div class="rdv-privacy" aria-label="${escapeHtml(messages.privacyTitle)}">
      <p class="rdv-privacy-text">${escapeHtml(notice)}</p>
      ${link}
    </div>
  `;
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (char) => {
    const entities = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      "\"": "&quot;",
      "'": "&#039;"
    };
    return entities[char] || char;
  });
}
